<template>
    <component
        :is="componentType"
        :id="id"
        :data-cy="dataCy"
        :data-testid="dataTestid"
        :href="componentType === 'a' ? href : undefined"
        :target="componentType === 'a' ? target : undefined"
        :to="componentType === 'router-link' ? to : undefined"
        :type="componentType === 'button' ? type : undefined"
        :class="buttonClasses"
        :disabled="componentType === 'button' ? (disabled || loading) : undefined"
        @click.stop="handleClick"
    >
        <span class="tw:flex tw:items-center tw:justify-center tw:gap-2 tw:w-full tw:text-center">
            <i v-if="loading" class="fas fa-spinner fa-spin"></i>
            <i v-else-if="iconName" class="tw:flex tw:items-center material-icons" :class="iconNameClass">{{ iconName }}</i>
            <i v-else-if="icon" class="tw:flex tw:items-center" :class="icon"></i>
            <slot></slot>
        </span>
    </component>
</template>

<script setup>
    import { computed } from 'vue';

    const props = defineProps({
        id: String,
        dataCy: String,
        dataTestid: String,
        disabled: Boolean,
        loading: Boolean,
        customClass: String,
        icon: String,
        iconName: String,
        iconNameClass: String,
        ignoreBaseClasses: {
            type: Boolean,
            default: false
        },
        as: {
            type: String,
            default: 'button',
            validator: v => ['button', 'router-link', 'a'].includes(v)
        },
        type: {
            type: String,
            default: 'button',
            validator: v => ['button', 'submit', 'reset'].includes(v)
        },
        to: {
            type: [String, Object],
            default: null
        },
        href: {
            type: String,
            default: null
        },
        target: {
            type: String,
            default: null
        },
        variant: {
            type: String,
            default: null,
            validator: v => ['default', 'primary', 'success', 'danger', 'warning', 'orange', 'orange-soft', 'ghost', 'dark', 'default-tiny', 'alt', 'default-soft', 'primary-soft', 'success-soft', 'danger-soft', 'warning-soft', 'dark-soft', 'ghost-soft', 'alt-soft'].includes(v)
        }
    });

    const emit = defineEmits(['click']);

    const componentType = computed(() => {
        if (props.as === 'router-link' && props.disabled) {
            return 'button'
        }
        if (props.as === 'a' && props.disabled) {
            return 'button'
        }
        return props.as
    })

    const variantClasses = computed(() => {
        if (props.ignoreBaseClasses) return '';

        const map = {
            'default': 'tw:bg-gray-200 tw:text-gray-800 tw:px-3 tw:py-2 tw:text-xs tw:sm:text-sm tw:rounded tw:hover:bg-gray-400',
            'primary':  'tw:bg-sky-500 tw:text-white tw:px-3 tw:py-2 tw:text-xs tw:sm:text-sm tw:rounded tw:hover:bg-sky-800',
            'success':  'tw:bg-emerald-500 tw:text-white tw:px-3 tw:py-2 tw:text-xs tw:sm:text-sm tw:rounded tw:hover:bg-emerald-800',
            'danger':   'tw:bg-red-500 tw:text-white tw:px-3 tw:py-2 tw:text-xs tw:sm:text-sm tw:rounded tw:hover:bg-red-800',
            'warning':  'tw:bg-amber-400 tw:text-white tw:px-3 tw:py-2 tw:text-xs tw:sm:text-sm tw:rounded tw:hover:bg-amber-600',
            'orange':  'tw:bg-orange-400 tw:text-white tw:px-3 tw:py-2 tw:text-xs tw:sm:text-sm tw:rounded tw:hover:bg-orange-600',
            'ghost':    'tw:bg-transparent tw:text-gray-500 tw:px-3 tw:py-2 tw:text-xs tw:sm:text-sm tw:rounded tw:hover:bg-gray-100 tw:hover:text-gray-900',
            'dark':     'tw:bg-gray-900 tw:text-white tw:px-3 tw:py-2 tw:text-xs tw:sm:text-sm tw:rounded tw:hover:bg-black',
            'default-tiny': 'tw:bg-white tw:text-slate-600 tw:px-3 tw:py-2 tw:text-xs tw:sm:text-sm tw:rounded tw:border tw:border-slate-200 tw:hover:bg-slate-50 ',
            'alt':     'tw:bg-[var(--altcolor)] tw:text-white tw:px-3 tw:py-2 tw:text-xs tw:sm:text-sm tw:rounded hover:tw:brightness-90',
            // Soft variants: bg claro, border visible, texto del color, hover más oscuro
            'default-soft': 'tw:bg-gray-50 tw:text-gray-700 tw:px-3 tw:py-2 tw:text-xs tw:sm:text-sm tw:rounded tw:border tw:border-gray-200 tw:hover:bg-gray-100 ',
            'primary-soft': 'tw:bg-sky-50 tw:text-sky-700 tw:px-3 tw:py-2 tw:text-xs tw:sm:text-sm tw:rounded tw:border tw:border-sky-200 tw:hover:bg-sky-100 ',
            'success-soft': 'tw:bg-emerald-50 tw:text-emerald-700 tw:px-3 tw:py-2 tw:text-xs tw:sm:text-sm tw:rounded tw:border tw:border-emerald-200 tw:hover:bg-emerald-100 ',
            'danger-soft':  'tw:bg-red-50 tw:text-red-700 tw:px-3 tw:py-2 tw:text-xs tw:sm:text-sm tw:rounded tw:border tw:border-red-200 tw:hover:bg-red-100 ',
            'warning-soft': 'tw:bg-amber-50 tw:text-amber-700 tw:px-3 tw:py-2 tw:text-xs tw:sm:text-sm tw:rounded tw:border tw:border-amber-200 tw:hover:bg-amber-100 ',
            'orange-soft': 'tw:bg-orange-50 tw:text-orange-400 tw:px-3 tw:py-2 tw:text-xs tw:sm:text-sm tw:rounded tw:border tw:border-orange-200 tw:hover:bg-orange-100 tw:hover:text-orange-600',
            'dark-soft':    'tw:bg-gray-100 tw:text-gray-800 tw:px-3 tw:py-2 tw:text-xs tw:sm:text-sm tw:rounded tw:border tw:border-gray-300 tw:hover:bg-gray-200 ',
            'ghost-soft':   'tw:bg-gray-50 tw:text-gray-600 tw:px-3 tw:py-2 tw:text-xs tw:rounded tw:border tw:border-gray-200 tw:hover:bg-gray-200 ',
            'alt-soft':     'tw:bg-[var(--altcolor)]/10 tw:text-[var(--altcolor)] tw:px-3 tw:py-2 tw:text-xs tw:sm:text-sm tw:rounded tw:border tw:border-[var(--altcolor)]/30 tw:hover:bg-[var(--altcolor)]/20 ',
        };
        return props.variant ? map[props.variant] : map['default'];
    });

    const buttonClasses = computed(() => {
        const baseClasses = props.ignoreBaseClasses ? '' : 'button'
        const disabledClasses = (props.disabled || props.loading) ? 'tw:opacity-50 tw:cursor-not-allowed tw:pointer-events-none' : ''
        return [baseClasses, variantClasses.value, disabledClasses, props.customClass].filter(Boolean).join(' ')
    })

    const handleClick = event => {
        if (props.disabled || props.loading) {
            event.preventDefault()
            event.stopPropagation()
            return
        }

        emit('click', event)
    }
</script>
